import React, { useMemo, useState, useCallback } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	ReferenceArea,
} from "recharts";

const ChartComponent = ({ data }) => {
	// Zoom state'leri
	const [refAreaLeft, setRefAreaLeft] = useState(null);
	const [refAreaRight, setRefAreaRight] = useState(null);
	const [xDomain, setXDomain] = useState(null);
	const [yDomain, setYDomain] = useState(null);
	const [isZooming, setIsZooming] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1);

	// Veriyi formatla ve filtrele
	const chartData = useMemo(() => {
		if (!data || !Array.isArray(data) || data.length === 0) {
			return [];
		}

		return data
			.filter((item) => item.time)
			.map((item) => {
				const date = new Date(item.time);
				return {
					time: date.getTime(), // Timestamp olarak sakla (sayısal değer)
					timeLabel: date.toLocaleString("tr-TR", {
						month: "short",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					}),
					hr: item.hr ?? null,
					rr: item.rr ?? null,
					hrv: item.hrv ?? null,
					sv: item.sv ?? null,
					movement: item.movement ?? null,
					relax: item.relax ?? null,
				};
			})
			.filter((item) => {
				// En az bir değer olan kayıtları filtrele
				return (
					item.hr !== null ||
					item.rr !== null ||
					item.hrv !== null ||
					item.sv !== null ||
					item.movement !== null ||
					item.relax !== null
				);
			})
			.sort((a, b) => a.time - b.time); // Zaman sırasına göre sırala
	}, [data]);

	// Y ekseni için dinamik max değer hesapla
	const maxValue = useMemo(() => {
		if (chartData.length === 0) return 180;

		const allValues = chartData
			.flatMap((item) => [
				item.hr,
				item.rr,
				item.hrv,
				item.sv,
				item.movement,
				item.relax,
			])
			.filter((val) => val !== null && !isNaN(val));

		return allValues.length > 0
			? Math.ceil(Math.max(...allValues) * 1.1) // %10 padding ekle
			: 180;
	}, [chartData]);

	// X ekseni için padding hesapla (verilerin kenarlardan uzak durması için)
	const xAxisPadding = useMemo(() => {
		if (chartData.length < 2) return 0;
		const times = chartData.map((item) => item.time);
		const minTime = Math.min(...times);
		const maxTime = Math.max(...times);
		const range = maxTime - minTime;
		// Toplam aralığın %3'ü kadar padding ekle
		return range * 0.03;
	}, [chartData]);

	// Zoom fonksiyonları
	const handleMouseDown = useCallback((e) => {
		if (e && e.activeLabel) {
			setRefAreaLeft(e.activeLabel);
			setIsZooming(true);
		}
	}, []);

	const handleMouseMove = useCallback(
		(e) => {
			if (isZooming && e && e.activeLabel) {
				setRefAreaRight(e.activeLabel);
			}
		},
		[isZooming]
	);

	const handleMouseUp = useCallback(() => {
		if (refAreaLeft && refAreaRight) {
			const left = Math.min(refAreaLeft, refAreaRight);
			const right = Math.max(refAreaLeft, refAreaRight);

			// Seçilen alandaki verileri bul
			const filteredData = chartData.filter(
				(item) => item.time >= left && item.time <= right
			);

			if (filteredData.length > 0) {
				// Y değerlerini bul
				const allYValues = filteredData
					.flatMap((item) => [
						item.hr,
						item.rr,
						item.hrv,
						item.sv,
						item.movement,
						item.relax,
					])
					.filter((val) => val !== null && !isNaN(val));

				if (allYValues.length > 0) {
					const minY = Math.min(...allYValues) * 0.9;
					const maxY = Math.max(...allYValues) * 1.1;
					setYDomain([Math.max(0, minY), maxY]);
				}

				setXDomain([left, right]);
				setZoomLevel((prev) => prev * 1.5);
			}
		}

		setRefAreaLeft(null);
		setRefAreaRight(null);
		setIsZooming(false);
	}, [refAreaLeft, refAreaRight, chartData]);

	const handleZoomReset = useCallback(() => {
		setXDomain(null);
		setYDomain(null);
		setZoomLevel(1);
	}, []);

	const zoomIn = useCallback(() => {
		if (chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMinTime = Math.min(...times);
		const dataMaxTime = Math.max(...times);
		const dataRange = dataMaxTime - dataMinTime;

		const newZoomLevel = Math.min(zoomLevel * 1.5, 10);
		setZoomLevel(newZoomLevel);

		const zoomRange = dataRange / newZoomLevel;
		const center = xDomain
			? (xDomain[0] + xDomain[1]) / 2
			: (dataMinTime + dataMaxTime) / 2;

		let newMin = center - zoomRange / 2;
		let newMax = center + zoomRange / 2;

		if (newMin < dataMinTime) {
			newMin = dataMinTime;
			newMax = dataMinTime + zoomRange;
		}
		if (newMax > dataMaxTime) {
			newMax = dataMaxTime;
			newMin = dataMaxTime - zoomRange;
		}

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData, zoomLevel]);

	const zoomOut = useCallback(() => {
		if (chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMinTime = Math.min(...times);
		const dataMaxTime = Math.max(...times);
		const dataRange = dataMaxTime - dataMinTime;

		const newZoomLevel = Math.max(zoomLevel / 1.5, 1);
		setZoomLevel(newZoomLevel);

		if (newZoomLevel <= 1) {
			setXDomain(null);
			setYDomain(null);
			return;
		}

		const zoomRange = dataRange / newZoomLevel;
		const center = xDomain
			? (xDomain[0] + xDomain[1]) / 2
			: (dataMinTime + dataMaxTime) / 2;

		let newMin = center - zoomRange / 2;
		let newMax = center + zoomRange / 2;

		if (newMin < dataMinTime) {
			newMin = dataMinTime;
			newMax = dataMinTime + zoomRange;
		}
		if (newMax > dataMaxTime) {
			newMax = dataMaxTime;
			newMin = dataMaxTime - zoomRange;
		}

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData, zoomLevel]);

	const panLeft = useCallback(() => {
		if (!xDomain || chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMinTime = Math.min(...times);
		const range = xDomain[1] - xDomain[0];
		const panAmount = range * 0.3;

		const newMin = Math.max(dataMinTime, xDomain[0] - panAmount);
		const newMax = newMin + range;

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData]);

	const panRight = useCallback(() => {
		if (!xDomain || chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMaxTime = Math.max(...times);
		const range = xDomain[1] - xDomain[0];
		const panAmount = range * 0.3;

		const newMax = Math.min(dataMaxTime, xDomain[1] + panAmount);
		const newMin = newMax - range;

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData]);

	const getZoomText = () => {
		if (zoomLevel <= 1) return "100%";
		return `${zoomLevel.toFixed(1)}x`;
	};

	if (chartData.length === 0) {
		return (
			<div
				style={{
					width: "100%",
					height: "500px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: "#718096",
					fontSize: "16px",
				}}
			>
				Veri bulunamadı. Lütfen tarih aralığı seçiniz.
			</div>
		);
	}

	// Renkler - projenin renk şemasına uygun
	const colors = {
		hr: "#e6194b", // Kırmızı
		rr: "#3cb44b", // Yeşil
		hrv: "#4363d8", // Mavi
		sv: "#ffe119", // Sarı
		movement: "#f58231", // Turuncu
		relax: "#911eb4", // Mor
	};

	// Tema uyumlu buton stilleri
	const buttonStyles = {
		base: {
			padding: "8px 14px",
			border: "none",
			borderRadius: "6px",
			cursor: "pointer",
			fontSize: "14px",
			fontWeight: "500",
			transition: "all 0.2s ease",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			minWidth: "40px",
		},
		primary: {
			backgroundColor: "#16a085",
			color: "white",
		},
		primaryDisabled: {
			backgroundColor: "#a0aec0",
			color: "white",
			cursor: "not-allowed",
		},
		secondary: {
			backgroundColor: "#4a5568",
			color: "white",
		},
		secondaryDisabled: {
			backgroundColor: "#cbd5e0",
			color: "white",
			cursor: "not-allowed",
		},
		danger: {
			backgroundColor: "#e53e3e",
			color: "white",
		},
		dangerDisabled: {
			backgroundColor: "#feb2b2",
			color: "white",
			cursor: "not-allowed",
		},
	};

	return (
		<div style={{ width: "100%", height: "600px" }}>
			{/* Zoom Kontrolleri */}
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					gap: "8px",
					marginBottom: "12px",
					flexWrap: "wrap",
					alignItems: "center",
					padding: "10px",
					backgroundColor: "#f7fafc",
					borderRadius: "8px",
					border: "1px solid #e2e8f0",
				}}
			>
				{/* Sol kaydırma */}
				<button
					onClick={panLeft}
					disabled={!xDomain}
					title="Sola Kaydır"
					style={{
						...buttonStyles.base,
						...(xDomain
							? buttonStyles.secondary
							: buttonStyles.secondaryDisabled),
					}}
				>
					◀
				</button>

				{/* Uzaklaştır */}
				<button
					onClick={zoomOut}
					disabled={zoomLevel <= 1}
					title="Uzaklaştır"
					style={{
						...buttonStyles.base,
						...(zoomLevel > 1
							? buttonStyles.primary
							: buttonStyles.primaryDisabled),
						fontSize: "18px",
						fontWeight: "bold",
					}}
				>
					−
				</button>

				{/* Zoom seviyesi göstergesi */}
				<div
					style={{
						padding: "8px 16px",
						backgroundColor: xDomain ? "#16a085" : "#edf2f7",
						color: xDomain ? "white" : "#4a5568",
						borderRadius: "6px",
						fontSize: "14px",
						minWidth: "70px",
						textAlign: "center",
						fontWeight: "600",
						border: xDomain ? "none" : "1px solid #e2e8f0",
					}}
				>
					🔍 {getZoomText()}
				</div>

				{/* Yakınlaştır */}
				<button
					onClick={zoomIn}
					disabled={zoomLevel >= 10}
					title="Yakınlaştır"
					style={{
						...buttonStyles.base,
						...(zoomLevel < 10
							? buttonStyles.primary
							: buttonStyles.primaryDisabled),
						fontSize: "18px",
						fontWeight: "bold",
					}}
				>
					+
				</button>

				{/* Sağ kaydırma */}
				<button
					onClick={panRight}
					disabled={!xDomain}
					title="Sağa Kaydır"
					style={{
						...buttonStyles.base,
						...(xDomain
							? buttonStyles.secondary
							: buttonStyles.secondaryDisabled),
					}}
				>
					▶
				</button>

				{/* Sıfırla */}
				<button
					onClick={handleZoomReset}
					disabled={!xDomain}
					title="Sıfırla"
					style={{
						...buttonStyles.base,
						...(xDomain ? buttonStyles.danger : buttonStyles.dangerDisabled),
						marginLeft: "8px",
					}}
				>
					↺ Sıfırla
				</button>
			</div>

			<ResponsiveContainer width="100%" height={480}>
				<LineChart
					data={chartData}
					margin={{
						top: 20,
						right: 30,
						left: 20,
						bottom: 60,
					}}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={() => {
						if (isZooming) {
							setRefAreaLeft(null);
							setRefAreaRight(null);
							setIsZooming(false);
						}
					}}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
					<XAxis
						type="number"
						dataKey="time"
						domain={xDomain || [
							(dataMin) => dataMin - xAxisPadding,
							(dataMax) => dataMax + xAxisPadding,
						]}
						tickFormatter={(tickItem) => {
							return new Date(tickItem).toLocaleString("tr-TR", {
								month: "short",
								day: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							});
						}}
						angle={-45}
						textAnchor="end"
						height={80}
						stroke="#4a5568"
						fontSize={12}
						allowDataOverflow
					/>
					<YAxis
						domain={yDomain || [0, maxValue]}
						stroke="#4a5568"
						fontSize={12}
						allowDataOverflow
					/>
					<Tooltip
						contentStyle={{
							backgroundColor: "#fff",
							border: "1px solid #e2e8f0",
							borderRadius: "8px",
							boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
							zIndex: 1000,
						}}
						labelStyle={{ color: "#2d3748", fontWeight: "600" }}
						labelFormatter={(value) => {
							return new Date(value).toLocaleString("tr-TR", {
								year: "numeric",
								month: "long",
								day: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							});
						}}
						wrapperStyle={{ zIndex: 1000 }}
						isAnimationActive={false}
					/>
					<Legend wrapperStyle={{ paddingTop: "20px" }} iconType="line" />
					<Line
						type="monotone"
						dataKey="hr"
						stroke={colors.hr}
						strokeWidth={2}
						dot={false}
						name="HR"
						connectNulls={false}
						isAnimationActive={false}
					/>
					<Line
						type="monotone"
						dataKey="rr"
						stroke={colors.rr}
						strokeWidth={2}
						dot={false}
						name="RR"
						connectNulls={false}
						isAnimationActive={false}
					/>
					<Line
						type="monotone"
						dataKey="hrv"
						stroke={colors.hrv}
						strokeWidth={2}
						dot={false}
						name="HRV"
						connectNulls={false}
						isAnimationActive={false}
					/>
					<Line
						type="monotone"
						dataKey="sv"
						stroke={colors.sv}
						strokeWidth={2}
						dot={false}
						name="SV"
						connectNulls={false}
						isAnimationActive={false}
					/>
					<Line
						type="monotone"
						dataKey="movement"
						stroke={colors.movement}
						strokeWidth={2}
						dot={false}
						name="Hareket"
						connectNulls={false}
						isAnimationActive={false}
					/>
					<Line
						type="monotone"
						dataKey="relax"
						stroke={colors.relax}
						strokeWidth={2}
						dot={false}
						name="Rahatlık"
						connectNulls={false}
						isAnimationActive={false}
					/>

					{/* Zoom seçim alanı */}
					{refAreaLeft && refAreaRight && (
						<ReferenceArea
							x1={refAreaLeft}
							x2={refAreaRight}
							strokeOpacity={0.3}
							fill="#16a085"
							fillOpacity={0.2}
						/>
					)}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};

export default ChartComponent;
